import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { D365TextField } from '@/components/D365FormControls';

import { getClientServiceLocations, searchClients } from '@/features/clients/api/clients';
import {
  createShoppingItem,
  getBodies,
  getShoppingItems,
  shoppingStatuses,
  updateShoppingItem,
  type ShoppingItem,
} from '@/features/shopping/api/shopping';

export function ShoppingItemFormPage() {
  const navigate = useNavigate();
  const { itemId } = useParams();
  const isNew = itemId === 'new';
  const id = isNew ? null : Number(itemId);
  const items = useQuery({
    queryKey: ['shopping'],
    queryFn: getShoppingItems,
    enabled: !isNew && Number.isInteger(id) && Number(id) > 0,
  });
  if ((!isNew && (!Number.isInteger(id) || Number(id) <= 0)) || items.isError)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This shopping item could not be loaded.
      </Alert>
    );
  if (!isNew && items.isLoading)
    return (
      <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Loading shopping item" />
      </Box>
    );
  const item = isNew ? null : items.data?.find((candidate) => candidate.id === id);
  if (!isNew && !item)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This shopping item could not be loaded.
      </Alert>
    );
  return (
    <ShoppingItemEditor
      key={id ?? 'new'}
      item={item ?? null}
      onClose={() => void navigate('/shopping-list')}
    />
  );
}

function ShoppingItemEditor({ item, onClose }: { item: ShoppingItem | null; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(item?.description ?? '');
  const [status, setStatus] = useState(item?.status ?? 0);
  const [clientId, setClientId] = useState(0);
  const [locationId, setLocationId] = useState(0);
  const [bodyId, setBodyId] = useState(item?.bodyId ?? 0);
  const [savedState, setSavedState] = useState({
    description: item?.description ?? '',
    status: item?.status ?? 0,
    bodyId: item?.bodyId ?? 0,
  });
  const clients = useQuery({
    queryKey: ['clients', 'shopping-select'],
    queryFn: () => searchClients(),
    enabled: !item,
  });
  const locations = useQuery({
    queryKey: ['clients', clientId, 'shopping-locations'],
    queryFn: () => getClientServiceLocations(clientId),
    enabled: !item && clientId > 0,
  });
  const bodies = useQuery({
    queryKey: ['clients', clientId, 'locations', locationId, 'bodies'],
    queryFn: () => getBodies(clientId, locationId),
    enabled: !item && clientId > 0 && locationId > 0,
  });
  const save = useMutation({
    mutationFn: () =>
      item
        ? updateShoppingItem(item.id, status)
        : createShoppingItem(description.trim(), status, bodyId),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['shopping'] });
      setSavedState({ description, status, bodyId });
      if (!item) void navigate(`/shopping-list/${saved.id}`, { replace: true });
    },
  });
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5, px: 1, py: 0.5 }}>
        <Stack direction="row" spacing={0.5} minHeight={44}>
          <Button sx={commandButtonSx} startIcon={<ArrowBackIcon />} onClick={onClose}>
            Back
          </Button>
          <Button
            sx={commandButtonSx}
            startIcon={<SaveOutlinedIcon />}
            disabled={
              save.isPending ||
              (!item && (!description.trim() || !bodyId)) ||
              (description === savedState.description &&
                status === savedState.status &&
                bodyId === savedState.bodyId)
            }
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, p: 2.5 }}>
        <Typography variant="h1">{item ? item.description : 'New Shopping List Item'}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {item
            ? `${item.clientName} · ${item.locationName} · ${item.bodyName}`
            : 'Create a supply request for a body of water.'}
        </Typography>
        <Stack spacing={2} maxWidth={620}>
          {!item && (
            <>
              <D365TextField
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                required
              />
              <D365TextField
                select
                label="Client"
                value={clientId}
                onChange={(event) => {
                  setClientId(Number(event.target.value));
                  setLocationId(0);
                  setBodyId(0);
                }}
              >
                <MenuItem value={0}>Select client</MenuItem>
                {(clients.data ?? []).map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </D365TextField>
              <D365TextField
                select
                label="Service location"
                value={locationId}
                disabled={!clientId}
                onChange={(event) => {
                  setLocationId(Number(event.target.value));
                  setBodyId(0);
                }}
              >
                <MenuItem value={0}>Select location</MenuItem>
                {(locations.data ?? []).map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </D365TextField>
              <D365TextField
                select
                label="Body of water"
                value={bodyId}
                disabled={!locationId}
                onChange={(event) => setBodyId(Number(event.target.value))}
              >
                <MenuItem value={0}>Select pool</MenuItem>
                {(bodies.data ?? []).map((body) => (
                  <MenuItem key={body.id} value={body.id}>
                    {body.name}
                  </MenuItem>
                ))}
              </D365TextField>
            </>
          )}
          <D365TextField
            select
            label="Status"
            value={status}
            onChange={(event) => setStatus(Number(event.target.value))}
          >
            {shoppingStatuses.map((label, index) => (
              <MenuItem key={label} value={index}>
                {label}
              </MenuItem>
            ))}
          </D365TextField>
          {save.isError && <Alert severity="error">The item could not be saved.</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};


